/**
 * Where focus goes when a sheet, pager or mode closes. Each surface records
 * what had focus as it opened. When that was inside another surface, it also
 * records that surface's own opener, because a menu item that opened a sheet
 * is usually gone by the time the sheet closes.
 */
export type FocusReturn = { element: HTMLElement | null; parent?: FocusReturn };

const HOST = "data-focus-host";
const hosts = new WeakMap<Element, FocusReturn>();

/** Call as a surface opens, before focus moves into it. */
export const captureOpener = (): FocusReturn => {
  const active =
    typeof document === "undefined" ? null : document.activeElement;
  const element =
    active instanceof HTMLElement && active !== document.body ? active : null;
  const host = element?.closest(`[${HOST}]`);
  return { element, parent: host ? hosts.get(host) : undefined };
};

/** Marks a surface's root, so anything opened from inside it can fall back to its opener. */
export const registerFocusHost = (root: Element | null, opener: FocusReturn) => {
  if (!root) return;
  root.setAttribute(HOST, "");
  hosts.set(root, opener);
};

/** The nearest recorded opener that's still on the page. */
export const resolveOpener = (opener?: FocusReturn) => {
  for (let step = opener; step; step = step.parent) {
    if (step.element?.isConnected) return step.element;
  }
  return null;
};

/** Focus the opener, unless something else (a dialog the surface started) has focus already. */
export const returnFocus = (opener: FocusReturn | undefined, focusVisible: boolean) => {
  const active = document.activeElement;
  if (active && active !== document.body && active.isConnected) return false;
  // An opener can be on the page yet unfocusable (inert, hidden); try the next.
  for (let step = opener; step; step = step.parent) {
    const target = step.element;
    if (!target?.isConnected) continue;
    // `focusVisible` is newer than TypeScript's FocusOptions; a browser that
    // ignores it falls back to its own :focus-visible.
    target.focus({ preventScroll: true, focusVisible } as FocusOptions);
    if (document.activeElement === target) return true;
  }
  return false;
};
